import React, { useState, useEffect, useMemo } from 'react';
import {
    MdHistory,
    MdDateRange,
    MdContentCut,
    MdPerson,
    MdAccessTime,
    MdWarning,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdCheckCircle,
    MdCancel,
    MdBlock
} from 'react-icons/md';

import { appointmentsAPI } from 'services/api';

// --- YARDIMCI FONKSİYONLAR ---
const getTodayDateString = () => {
    const today = new Date();
    // Saat farklarından etkilenmemesi için yerel tarihi YYYY-MM-DD formatına çevirme
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getStatusConfig = (status) => {
    switch (status) {
        case 'completed':
            return { text: 'Tamamlandı', icon: <MdCheckCircle />, classes: 'bg-green-50 text-green-500 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-800' };
        case 'cancelled':
            return { text: 'İptal Edildi', icon: <MdCancel />, classes: 'bg-red-50 text-red-500 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-800' };
        case 'no_show':
            return { text: 'Gelmedi', icon: <MdWarning />, classes: 'bg-amber-50 text-amber-500 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-800' };
        case 'rejected':
            return { text: 'Reddedildi', icon: <MdBlock />, classes: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10' };
        default:
            return { text: 'Bilinmiyor', icon: <MdWarning />, classes: 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10' };
    }
};

// --- SKELETON LOADER ---
const HistorySkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse">
        {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
        ))}
    </div>
);

// --- ANA BİLEŞEN ---
const StaffAppointmentHistory = () => {
    // --- STATELER (WORK-LOGIC) ---
    const [groupedAppointments, setGroupedAppointments] = useState({});
    const [selectedDate, setSelectedDate] = useState(getTodayDateString());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sayfalama (Pagination)
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // --- API VE VERİ İŞLEME (WORK-LOGIC BİREBİR) ---
    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await appointmentsAPI.getMyAppointments();

                if (response.success) {
                    const apps = response.appointments || [];

                    // Gelecek ve bekleyen randevuları çıkar, sadece geçmiş/tamamlanan işlemler kalsın
                    const pastAppointments = apps.filter(
                        (app) => !['pending', 'approved'].includes(app.status)
                    );

                    // Tarihlere göre grupla (YYYY-MM-DD)
                    const grouped = pastAppointments.reduce((acc, app) => {
                        // "2023-10-25T14:30:00.000000Z" -> "2023-10-25"
                        const date = app.appointment_date ? app.appointment_date.split('T')[0] : null;
                        if (date) {
                            if (!acc[date]) acc[date] = [];
                            acc[date].push(app);
                        }
                        return acc;
                    }, {});

                    // Her günün içindeki randevuları saate göre azalan (yeniden eskiye) sırala
                    Object.keys(grouped).forEach((dateKey) => {
                        grouped[dateKey].sort((a, b) => {
                            const timeA = a.appointment_time || "";
                            const timeB = b.appointment_time || "";
                            return timeB.localeCompare(timeA);
                        });
                    });

                    setGroupedAppointments(grouped);
                } else {
                    setError('İşlem geçmişi alınırken bir sorun oluştu.');
                }
            } catch (err) {
                console.error("Geçmiş çekilemedi:", err);
                setError('Sunucu bağlantısında hata oluştu.');
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, []);

    // --- SAYFALAMA HESAPLARI ---
    // Sadece seçili tarihe ait olanları al
    const currentDateAppointments = useMemo(() => {
        return groupedAppointments[selectedDate] || [];
    }, [groupedAppointments, selectedDate]);

    const totalPages = Math.max(1, Math.ceil(currentDateAppointments.length / PAGE_SIZE));

    const currentPaginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        return currentDateAppointments.slice(startIndex, startIndex + PAGE_SIZE);
    }, [currentDateAppointments, currentPage]);

    // Tarih değiştiğinde sayfayı 1'e sıfırla
    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
        setCurrentPage(1);
    };

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK VE TARİH FİLTRESİ */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">

                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdHistory className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">İşlem Geçmişi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Geçmişte tamamlanmış, iptal edilmiş veya gelinmemiş tüm randevularınız.
                        </p>
                    </div>
                </div>

                {/* Tarih Seçici (Custom Tailwind Input) */}
                <div className="relative w-full md:w-64">
                    <MdDateRange className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="w-full h-12 pl-11 pr-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all shadow-sm cursor-pointer"
                    />
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. LİSTELEME ALANI */}
            <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">

                {/* Tablo Başlığı */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white">
                        {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })} İşlemleri
                    </h5>
                    <span className="px-3 py-1 bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 rounded-lg text-sm font-bold">
            {currentDateAppointments.length} Kayıt
          </span>
                </div>

                <div className="p-4 md:p-6">
                    {loading ? (
                        <HistorySkeleton />
                    ) : currentDateAppointments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 opacity-60">
                            <MdHistory className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                            <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-1">Bu tarihte işlem bulunmuyor.</h5>
                            <p className="text-sm font-medium text-gray-500 text-center">Farklı bir tarih seçerek geçmiş işlemlerinize göz atabilirsiniz.</p>
                        </div>
                    ) : (
                        <>
                            {/* --- MASAÜSTÜ GÖRÜNÜM (TABLO) --- */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                    <tr>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Saat</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Müşteri</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Hizmet</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10 text-right">Durum</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {currentPaginatedData.map((appt) => {
                                        const statusConfig = getStatusConfig(appt.status);
                                        return (
                                            <tr key={appt.id} className="hover:bg-gray-50/50 dark:hover:bg-navy-900/50 transition-colors border-b border-gray-50 dark:border-white/5 last:border-none">
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2 text-navy-700 dark:text-white font-black text-sm">
                                                        <MdAccessTime className="text-brand-500 text-lg" />
                                                        {appt.appointment_time?.substring(0, 5) || "-"}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-8 w-8 rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/10 flex items-center justify-center font-bold">
                                                            {(appt.customer?.name?.charAt(0) || 'M')}
                                                        </div>
                                                        <span className="text-sm font-bold text-navy-700 dark:text-white">
                                {appt.customer?.name} {appt.customer?.surname}
                              </span>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-300">
                                                        <MdContentCut className="text-gray-400" />
                                                        {appt.service?.name || "Bilinmeyen Hizmet"}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-right">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold uppercase tracking-wide ${statusConfig.classes}`}>
                              {statusConfig.icon} {statusConfig.text}
                            </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* --- MOBİL GÖRÜNÜM (KARTLAR) --- */}
                            <div className="flex flex-col gap-4 md:hidden">
                                {currentPaginatedData.map((appt) => {
                                    const statusConfig = getStatusConfig(appt.status);
                                    return (
                                        <div key={appt.id} className="flex flex-col p-5 rounded-2xl bg-gray-50 dark:bg-navy-900 border border-gray-100 dark:border-white/5">
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-10 w-10 rounded-full bg-brand-50 text-brand-500 dark:bg-brand-500/10 flex items-center justify-center font-bold text-lg">
                                                        {(appt.customer?.name?.charAt(0) || 'M')}
                                                    </div>
                                                    <div className="flex flex-col">
                            <span className="text-base font-bold text-navy-700 dark:text-white leading-tight">
                              {appt.customer?.name} {appt.customer?.surname}
                            </span>
                                                        <span className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">
                              <MdContentCut /> {appt.service?.name || "Bilinmiyor"}
                            </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between mt-2 pt-3 border-t border-gray-200 dark:border-white/10">
                                                <div className="flex items-center gap-1.5 text-navy-700 dark:text-white font-black text-sm">
                                                    <MdAccessTime className="text-brand-500 text-lg" />
                                                    {appt.appointment_time?.substring(0, 5) || "-"}
                                                </div>
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${statusConfig.classes}`}>
                          {statusConfig.icon} {statusConfig.text}
                        </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* 3. SAYFALAMA (PAGINATION) */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between p-6 border-t border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20 gap-4">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              <span className="font-bold text-navy-700 dark:text-white">{currentDateAppointments.length}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, currentDateAppointments.length)}</span> gösteriliyor.
            </span>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowLeft className="text-2xl" />
                            </button>

                            <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowRight className="text-2xl" />
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default StaffAppointmentHistory;